/**
 * Gap-model-weergave — het Gap-diagram als visueel mini-conceptmodel
 * (roadmap-tijdreizen.md §6.3, backlog "modelleer-artefact-weergave").
 *
 * Toont alléén de geraakte zaken als een conceptmodel:
 * - elk gewijzigd object = een concept-box (status-kleur) met de namen van de
 *   geraakte attributen erin, en een gele post-it ernaast met de oud→nieuw-data
 *   (naam/type/stereotype staan in de box-header, niet als attribuut);
 * - gewijzigde relaties = de ÉCHTE ArchiMate-relatie-lijn (markers/lijnstijl per
 *   type via `applyRelationStyle`), getint naar status;
 * - ongewijzigde eindpunt-concepten = grijze ghost-box als anker.
 *
 * Boxes/ghosts schalen mee met hun tekst (geen afkapping). Auto-layout via dagre;
 * React Flow levert pan/zoom + edge-routing. Attribuut-labels/-waarden komen via
 * de gedeelde `gapDiffFormat`-helpers, identiek aan de tabel-weergave.
 */
import dagre from "@dagrejs/dagre";
import {
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  BranchDiffConceptMeta,
  BranchDiffEntity,
  BranchDiffRelationMeta,
  BranchDiffResponse,
  BranchDiffStatus,
} from "../../api/client";
import { resolveBilingual } from "../../registry";
import { useWorkspaceData } from "../../state/workspace";
import { EdgeMarkers } from "./EdgeMarkers";
import { applyRelationStyle } from "./edgeStyles";
import {
  attrInfo,
  conceptTypeLabel,
  formatAttrValue,
  isEmptyValue,
  isTbdPlaceholder,
} from "./gapDiffFormat";

interface GapModelCanvasProps {
  data: BranchDiffResponse;
  lang: string;
}

const STATUS_BORDER: Record<BranchDiffStatus, string> = {
  added: "#16a34a",
  changed: "#d97706",
  deleted: "#dc2626",
};
const STATUS_BG: Record<BranchDiffStatus, string> = {
  added: "#f0fdf4",
  changed: "#fffbeb",
  deleted: "#fef2f2",
};
// Carrier-box: het Concept zelf is ongewijzigd maar draagt wél een gewijzigd
// Attribuut/Identifier — neutrale grijze stijl zodat 't niet als "changed" leest.
const NEUTRAL_BORDER = "#94a3b8";
const NEUTRAL_BG = "#f8fafc";

// Structuur-velden staan al in de box-header (naam + «stereotype»/type) → niet
// als geraakt attribuut tonen. `source`/`target`/`label` horen bij de lijn zelf.
const EXCLUDED_FIELDS = new Set(["source", "target", "name", "type_id", "stereotypes", "label"]);

function relMeta(e: BranchDiffEntity): BranchDiffRelationMeta | null {
  const m = e.meta as BranchDiffRelationMeta | undefined;
  return m && m.kind === "relation" ? m : null;
}
function conMeta(e: BranchDiffEntity): BranchDiffConceptMeta | null {
  const m = e.meta as BranchDiffConceptMeta | undefined;
  return m && m.kind === "concept" ? m : null;
}

// ---- node data ------------------------------------------------------------

interface AffectedAttr {
  field: string;
  before: string;
  after: string;
}
/** Een gewijzigd Attribuut/Identifier, genest onder z'n eigenaar-Concept. */
interface SubChange {
  key: string;
  name: string;
  typeLabel: string;
  status: BranchDiffStatus;
  attrs: AffectedAttr[];
}
interface ConceptNodeData {
  name: string;
  typeLabel: string;
  status: BranchDiffStatus;
  attrs: AffectedAttr[];
  boxW: number;
  postitW: number;
  /** Genest getoonde Attribuut/Identifier-wijzigingen (parent_id-eigendom). */
  subChanges: SubChange[];
  /** Carrier-box: Concept zelf ongewijzigd, alleen z'n sub-elementen wijzigden. */
  neutral?: boolean;
  [key: string]: unknown;
}
interface GhostNodeData {
  name: string;
  [key: string]: unknown;
}

const HEADER_H = 40;
const ATTR_ROW_H = 16;

/** Geraakte attributen van een diff-entity (excl. structuur-velden), met
 *  tabel-identieke labels/waarden. */
function affectedAttrs(
  e: BranchDiffEntity,
  lang: string,
  resolveRef?: (id: string) => string | undefined,
): AffectedAttr[] {
  const meta = e.meta as { typeId?: string; stereotypes?: string[] } | undefined;
  const typeId = meta?.typeId;
  const stereos = meta?.stereotypes;
  const keepRaw = (before: unknown, after: unknown): boolean => {
    if (e.status === "added") return !(isEmptyValue(after) || isTbdPlaceholder(after));
    if (e.status === "deleted") return !(isEmptyValue(before) || isTbdPlaceholder(before));
    return !(isEmptyValue(after) || isTbdPlaceholder(after));
  };
  const out: AffectedAttr[] = [];
  for (const d of e.deltas) {
    if (EXCLUDED_FIELDS.has(d.field)) continue;
    if (d.field === "attributes") {
      const before =
        d.before && typeof d.before === "object" ? (d.before as Record<string, unknown>) : {};
      const after =
        d.after && typeof d.after === "object" ? (d.after as Record<string, unknown>) : {};
      const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
      for (const k of keys) {
        const b = before[k] ?? null;
        const a = after[k] ?? null;
        if (e.status === "changed" && JSON.stringify(b) === JSON.stringify(a)) continue;
        if (!keepRaw(b, a)) continue;
        const info = attrInfo(k, stereos, typeId, lang);
        out.push({
          field: info.field,
          before: formatAttrValue(b, info.schema, lang, resolveRef),
          after: formatAttrValue(a, info.schema, lang, resolveRef),
        });
      }
      continue;
    }
    if (!keepRaw(d.before, d.after)) continue;
    out.push({
      field: d.field,
      before: formatAttrValue(d.before, null, lang),
      after: formatAttrValue(d.after, null, lang),
    });
  }
  return out;
}

// ---- tekst-breedte-schatting (box past zich aan de tekst aan) --------------

function estBoxWidth(name: string, typeLabel: string, attrs: AffectedAttr[]): number {
  const lines = [
    name.length * 7.2,
    typeLabel.length * 5.9,
    ...attrs.map((a) => a.field.length * 6.2),
  ];
  return Math.min(300, Math.max(110, Math.ceil(Math.max(...lines)) + 26));
}
function estPostitWidth(attrs: AffectedAttr[]): number {
  if (attrs.length === 0) return 0;
  const widths = attrs.flatMap((a) => [
    a.field.length * 5.6,
    (a.before.length + a.after.length + 4) * 5.2,
  ]);
  return Math.min(180, Math.max(90, Math.ceil(Math.max(...widths)) + 16));
}
/** Hoogte-schatting van een relatie-post-it: ~2 regels per attribuut. */
function estPostitHeight(attrs: AffectedAttr[]): number {
  return attrs.length * 28 + 12;
}
function estGhostWidth(name: string): number {
  return Math.min(260, Math.max(90, Math.ceil(name.length * 7.2) + 26));
}

// ---- custom nodes ---------------------------------------------------------

function GapConceptNode({ data }: NodeProps) {
  const d = data as ConceptNodeData;
  const border = d.neutral ? NEUTRAL_BORDER : STATUS_BORDER[d.status];
  const bg = d.neutral ? NEUTRAL_BG : STATUS_BG[d.status];
  return (
    <div className="flex items-start gap-2">
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <div
        className="rounded-md border-2 shadow-sm"
        style={{ borderColor: border, background: bg, width: d.boxW }}
      >
        <div className="border-b px-2 py-1" style={{ borderColor: border }}>
          <div className="break-words font-semibold text-[12px] text-slate-900">
            {d.name || "—"}
          </div>
          {d.typeLabel && (
            <div className="break-words text-[10px] text-slate-500">{d.typeLabel}</div>
          )}
        </div>
        {d.attrs.length > 0 && (
          <ul className="px-2 py-1">
            {d.attrs.map((a) => (
              <li key={a.field} className="break-words text-[11px] text-slate-700">
                {a.field}
              </li>
            ))}
          </ul>
        )}
        {d.subChanges.length > 0 && (
          <div className="border-t px-2 py-1" style={{ borderColor: border }}>
            {d.subChanges.map((s) => (
              <div key={s.key} className="mb-1 last:mb-0">
                <div className="flex items-baseline gap-1">
                  <span
                    className="inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ background: STATUS_BORDER[s.status] }}
                  />
                  <span className="break-words font-medium text-[11px] text-slate-800">
                    {s.name || "—"}
                  </span>
                  {s.typeLabel && (
                    <span className="break-words text-[9px] text-slate-400">{s.typeLabel}</span>
                  )}
                </div>
                {s.attrs.map((a) => (
                  <div key={a.field} className="break-words pl-3 text-[10px] text-slate-600">
                    {a.field}:{" "}
                    {s.status === "added" ? (
                      <span>{a.after}</span>
                    ) : s.status === "deleted" ? (
                      <span className="line-through">{a.before}</span>
                    ) : (
                      <span>
                        <span className="line-through opacity-60">{a.before}</span> → {a.after}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      {d.attrs.length > 0 && (
        <div
          className="shrink-0 rounded-sm px-1.5 py-1 text-[9px] shadow-sm"
          style={{ background: "#fef9c3", color: "#713f12", width: d.postitW }}
        >
          {d.attrs.map((a) => (
            <div key={a.field} className="mb-0.5 leading-tight">
              <div className="break-words font-medium">{a.field}</div>
              <div className="break-words">
                {d.status === "added" ? (
                  <span>{a.after}</span>
                ) : d.status === "deleted" ? (
                  <span className="line-through">{a.before}</span>
                ) : (
                  <span>
                    <span className="line-through opacity-60">{a.before}</span> → {a.after}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </div>
  );
}

function GapGhostNode({ data }: NodeProps) {
  const d = data as GhostNodeData;
  return (
    <div className="rounded-md border border-slate-300 border-dashed bg-slate-50 px-2 py-1 text-center">
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <span className="break-words text-[11px] text-slate-400">{d.name || "—"}</span>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </div>
  );
}

interface RelPostitData {
  attrs: AffectedAttr[];
  status: BranchDiffEntity["status"];
  width: number;
  title?: string;
}

/** Post-it met de gewijzigde attributen van een RELATIE — los node bij het
 *  edge-midpoint, zodat het edge-label kort blijft (alleen de relatie-naam). */
function GapRelPostitNode({ data }: NodeProps) {
  const d = data as unknown as RelPostitData;
  return (
    <div
      className="rounded-sm px-1.5 py-1 text-[9px] shadow-sm"
      style={{ background: "#fef9c3", color: "#713f12", width: d.width }}
    >
      {d.title && (
        <div className="mb-0.5 break-words border-b border-amber-300 pb-0.5 font-semibold">
          {d.title}
        </div>
      )}
      {d.attrs.map((a) => (
        <div key={a.field} className="mb-0.5 leading-tight">
          <div className="break-words font-medium">{a.field}</div>
          <div className="break-words">
            {d.status === "added" ? (
              <span>{a.after}</span>
            ) : d.status === "deleted" ? (
              <span className="line-through">{a.before}</span>
            ) : (
              <span>
                <span className="line-through opacity-60">{a.before}</span> → {a.after}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const nodeTypes = {
  gapConcept: GapConceptNode,
  gapGhost: GapGhostNode,
  gapRelPostit: GapRelPostitNode,
};

// ---- layout ---------------------------------------------------------------

interface PostitLayout {
  id: string;
  source: string;
  target: string;
  width: number;
  height: number;
  data: RelPostitData;
}

function runLayout(nodes: Node[], edges: Edge[], postits: PostitLayout[]): Node[] {
  const g = new dagre.graphlib.Graph({ multigraph: true });
  // Ruimer dan voorheen (nodesep/ranksep) + post-its als edge-label met eigen
  // afmetingen, zodat dagre er ruimte voor reserveert i.p.v. overlap.
  g.setGraph({ rankdir: "LR", nodesep: 80, ranksep: 220, marginx: 24, marginy: 24 });
  g.setDefaultEdgeLabel(() => ({}));
  for (const n of nodes) g.setNode(n.id, { width: n.width ?? 200, height: n.height ?? 64 });
  const postitFor = (e: Edge) =>
    postits.find((p) => p.source === e.source && p.target === e.target);
  for (const e of edges) {
    const p = postitFor(e);
    // Post-it → reserveer post-it-afmetingen. Geen post-it maar wél een
    // naam-label → reserveer ruimte voor dat label zodat het niet over de
    // lijn of een ander label valt.
    const lbl = typeof e.label === "string" ? e.label : "";
    const dims = p
      ? { width: p.width, height: p.height, labelpos: "c" as const }
      : lbl
        ? { width: lbl.length * 6 + 16, height: 22, labelpos: "c" as const }
        : {};
    g.setEdge(e.source, e.target, dims, e.id);
  }
  dagre.layout(g);
  const out: Node[] = nodes.map((n) => {
    const p = g.node(n.id);
    const w = n.width ?? 200;
    const h = n.height ?? 64;
    return { ...n, position: { x: p.x - w / 2, y: p.y - h / 2 } };
  });
  // Post-its op de door dagre gereserveerde edge-label-positie plaatsen.
  for (const e of edges) {
    const p = postitFor(e);
    if (!p) continue;
    const ge = g.edge(e.source, e.target, e.id) as { x?: number; y?: number } | undefined;
    if (!ge || ge.x == null || ge.y == null) continue;
    out.push({
      id: p.id,
      type: "gapRelPostit",
      position: { x: ge.x - p.width / 2, y: ge.y - p.height / 2 },
      width: p.width,
      draggable: true,
      data: p.data as unknown as Record<string, unknown>,
    });
  }
  return out;
}

// ---- main -----------------------------------------------------------------

function GapModelInner({ data, lang }: GapModelCanvasProps) {
  const { t } = useTranslation();

  const { nodes, edges } = useMemo(() => {
    // Resolve reference-attributen (bv. `attribute_type_ref`) naar de naam van
    // het gerefereerde concept i.p.v. de rauwe UUID.
    const store = useWorkspaceData.getState().store;
    const resolveRef = (id: string): string | undefined => {
      const c = store.getConcept(id);
      return c ? resolveBilingual(c.name, lang) || undefined : undefined;
    };

    // Sharp Attribuut/Identifier horen bij hun eigenaar-Concept (parent_id) en
    // worden genest onder dat Concept getoond — niet als losse box.
    const OWNED_SUB_ELEMENTS = new Set(["sharp.Attribute", "sharp.Identifier"]);
    const visibleConcepts = data.concepts.filter(
      (e) => !OWNED_SUB_ELEMENTS.has(conMeta(e)?.typeId ?? ""),
    );
    const subElements = data.concepts.filter((e) =>
      OWNED_SUB_ELEMENTS.has(conMeta(e)?.typeId ?? ""),
    );

    const conceptById = new Map<string, BranchDiffEntity>();
    for (const e of visibleConcepts) conceptById.set(e.logicalId, e);

    // Gewijzigde sub-elementen groeperen op hun eigenaar-Concept (logische id).
    const subByParent = new Map<string, SubChange[]>();
    const parentNameById = new Map<string, { nl?: string; en?: string }>();
    for (const e of subElements) {
      const meta = conMeta(e);
      const attrs = e.status === "deleted" ? [] : affectedAttrs(e, lang, resolveRef);
      // Ongewijzigd attribuut zonder attribuut-delta's overslaan (added/deleted
      // zijn per definitie een wijziging: het sub-element kwam/ging).
      if (e.status === "changed" && attrs.length === 0) continue;
      const parentId = meta?.parentId ?? "";
      if (!parentId) continue;
      if (meta?.parentName) parentNameById.set(parentId, meta.parentName);
      const sub: SubChange = {
        key: e.logicalId,
        name: (meta?.name ? resolveBilingual(meta.name, lang) : (e.label ?? "")) || e.logicalId,
        typeLabel: meta ? conceptTypeLabel(meta.typeId, meta.stereotypes, lang) : "",
        status: e.status,
        attrs,
      };
      const list = subByParent.get(parentId) ?? [];
      list.push(sub);
      subByParent.set(parentId, list);
    }

    const subHeight = (subs: SubChange[]): number =>
      subs.reduce((n, s) => n + ATTR_ROW_H + s.attrs.length * ATTR_ROW_H, 0) +
      (subs.length ? 8 : 0);
    const subWidth = (subs: SubChange[]): number => {
      if (subs.length === 0) return 0;
      const w = subs.flatMap((s) => [
        (s.name.length + s.typeLabel.length) * 5.6,
        ...s.attrs.map((a) => (a.field.length + a.before.length + a.after.length + 6) * 5),
      ]);
      return Math.min(320, Math.max(...w) + 28);
    };

    const rawNodes: Node[] = [];
    for (const e of visibleConcepts) {
      const meta = conMeta(e);
      // Verwijderde objecten: geen attribuut-post-it — het object is wég, de
      // status-kleur (rood) zegt al genoeg.
      const attrs = e.status === "deleted" ? [] : affectedAttrs(e, lang, resolveRef);
      const name =
        (meta?.name ? resolveBilingual(meta.name, lang) : (e.label ?? "")) || e.logicalId;
      const typeLabel = meta ? conceptTypeLabel(meta.typeId, meta.stereotypes, lang) : "";
      // Verwijderd Concept: ook de (verwijderde) sub-elementen niet nesten —
      // het hele object is wég, de rode kop zegt genoeg.
      const subChanges = e.status === "deleted" ? [] : (subByParent.get(e.logicalId) ?? []);
      const boxW = Math.max(estBoxWidth(name, typeLabel, attrs), subWidth(subChanges));
      const postitW = estPostitWidth(attrs);
      rawNodes.push({
        id: e.logicalId,
        type: "gapConcept",
        position: { x: 0, y: 0 },
        width: boxW + (postitW > 0 ? postitW + 8 : 0),
        height: Math.max(64, HEADER_H + attrs.length * ATTR_ROW_H + subHeight(subChanges) + 12),
        data: {
          name,
          typeLabel,
          status: e.status,
          attrs,
          boxW,
          postitW,
          subChanges,
        } satisfies ConceptNodeData,
      });
    }

    // Carrier-boxen: sub-element wijzigde maar z'n Concept staat niet in de diff
    // (ongewijzigd). Toon 't Concept alsnog in neutrale stijl met de nesting.
    for (const [parentId, subs] of subByParent) {
      if (conceptById.has(parentId)) continue;
      const pname = parentNameById.get(parentId);
      const name = (pname ? resolveBilingual(pname, lang) : "") || parentId;
      const boxW = Math.max(estBoxWidth(name, "", []), subWidth(subs));
      rawNodes.push({
        id: parentId,
        type: "gapConcept",
        position: { x: 0, y: 0 },
        width: boxW,
        height: Math.max(64, HEADER_H + subHeight(subs) + 12),
        data: {
          name,
          typeLabel: "",
          status: "changed",
          attrs: [],
          boxW,
          postitW: 0,
          subChanges: subs,
          neutral: true,
        } satisfies ConceptNodeData,
      });
      conceptById.set(parentId, { logicalId: parentId, status: "changed", deltas: [] });
    }

    const usedGhosts = new Set<string>();
    const ensureGhost = (id: string, label: string) => {
      if (conceptById.has(id) || usedGhosts.has(id)) return;
      usedGhosts.add(id);
      rawNodes.push({
        id,
        type: "gapGhost",
        position: { x: 0, y: 0 },
        width: estGhostWidth(label),
        height: 40,
        data: { name: label } satisfies GhostNodeData,
      });
    };

    const rawEdges: Edge[] = [];
    const relPostits: PostitLayout[] = [];
    for (const e of data.relations) {
      const meta = relMeta(e);
      if (!meta?.sourceConceptId || !meta?.targetConceptId) continue;
      const src = meta.sourceConceptId;
      const tgt = meta.targetConceptId;
      ensureGhost(src, meta.sourceLabel ? resolveBilingual(meta.sourceLabel, lang) : src);
      ensureGhost(tgt, meta.targetLabel ? resolveBilingual(meta.targetLabel, lang) : tgt);
      // Verwijderde relaties: geen post-it — de rode lijn zegt al genoeg.
      const attrs = e.status === "deleted" ? [] : affectedAttrs(e, lang, resolveRef);
      const relName = e.label && e.label !== meta.typeId ? e.label : "";
      // Gewijzigde relatie-attributen → aparte post-it bij het edge-midpoint
      // (net als de concept-boxen). De relatie-naam staat als kop óp de post-it,
      // niet meer als los edge-label, zodat die tekst niet over de lijn valt.
      const hasPostit = attrs.length > 0;
      if (hasPostit) {
        const pw = Math.max(estPostitWidth(attrs), relName ? relName.length * 6 + 16 : 0);
        relPostits.push({
          id: `${e.logicalId}__postit`,
          source: src,
          target: tgt,
          width: pw,
          height: estPostitHeight(attrs) + (relName ? 18 : 0),
          data: { attrs, status: e.status, width: pw, ...(relName ? { title: relName } : {}) },
        });
      }
      // Edge-label alléén tonen als er GEEN post-it is (anders dubbel + overlap).
      const label = hasPostit ? "" : relName;
      // De ÉCHTE ArchiMate-relatie-lijn (markers + lijnstijl per type), daarna
      // de stroke getint naar status zodat de diff leesbaar blijft.
      let edge: Edge = {
        id: e.logicalId,
        source: src,
        target: tgt,
        label: label || undefined,
        labelStyle: { fontSize: 10, fill: "#475569" },
        labelBgStyle: { fill: "#ffffff", fillOpacity: 0.85 },
      };
      edge = applyRelationStyle(edge, meta.typeId, meta.attributes, meta.stereotypes);
      edge.style = {
        ...edge.style,
        stroke: STATUS_BORDER[e.status],
        strokeWidth: 1.8,
        ...(e.status === "deleted" ? { opacity: 0.65 } : {}),
      };
      rawEdges.push(edge);
    }

    // Eén layout-pass: dagre reserveert ruimte voor de post-its via edge-label-
    // afmetingen, zodat ze niet over boxen of elkaar heen vallen.
    return { nodes: runLayout(rawNodes, rawEdges, relPostits), edges: rawEdges };
  }, [data, lang]);

  if (nodes.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <p className="text-slate-500 text-xs">{t("gapDiff.empty")}</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-white">
      <EdgeMarkers />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        nodesConnectable={false}
        edgesFocusable={false}
        minZoom={0.2}
      />
    </div>
  );
}

export function GapModelCanvas(props: GapModelCanvasProps) {
  return (
    <ReactFlowProvider>
      <GapModelInner {...props} />
    </ReactFlowProvider>
  );
}
